import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, User, AlertCircle, Loader2, Phone, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Storage keys
const VENDOR_CODE_KEY = 'bombazoo_vendor_code';
const VENDOR_INFO_KEY = 'bombazoo_vendor_info';

// Helper functions to get/set vendor code
export const getStoredVendorCode = () => {
  return localStorage.getItem(VENDOR_CODE_KEY);
};

export const getStoredVendorInfo = () => {
  try {
    const info = localStorage.getItem(VENDOR_INFO_KEY);
    return info ? JSON.parse(info) : null;
  } catch {
    return null;
  }
};

export const setVendorCode = (code, vendorInfo = null) => {
  localStorage.setItem(VENDOR_CODE_KEY, code);
  if (vendorInfo) {
    localStorage.setItem(VENDOR_INFO_KEY, JSON.stringify(vendorInfo));
  }
};

export const clearVendorCode = () => {
  localStorage.removeItem(VENDOR_CODE_KEY);
  localStorage.removeItem(VENDOR_INFO_KEY);
};

// Validate vendor code with backend
export const validateVendorCode = async (code) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/vendors/validate-code/${code}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error validating vendor code:', error);
    return { valid: false, message: 'Error de conexión' };
  }
};

export default function VendorCodeModal({ isOpen, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Ingresa un código de vendedor');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await validateVendorCode(code.toUpperCase());
    
    if (result.valid) {
      // Save vendor code and info
      setVendorCode(result.vendor_code, {
        name: result.vendor_name,
        slug: result.vendor_slug,
        id: result.vendor_id
      });
      
      toast.success(`¡Código válido! Comprando con ${result.vendor_name}`);
      onSuccess?.(result);
      onClose();
    } else {
      setError(result.message || 'Código no válido');
      
      // If vendor is inactive, show the code anyway
      if (result.vendor_code) {
        setError(`${result.message}. Código: ${result.vendor_code}`);
      }
    }
    
    setLoading(false);
  };

  const handleNoCode = () => {
    navigate('/necesitas-agente');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="w-6 h-6 text-teal-500" />
            Código de Vendedor Requerido
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Para comprar necesitas el código de un agente autorizado de Bombazoo.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Ingresa tu código de vendedor
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Ej: JUAN-PEREZ"
              className="text-lg tracking-wide font-mono uppercase"
              disabled={loading}
              autoFocus
            />
            {error && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {error}
              </p>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={loading || !code.trim()}
            className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Verificar Código
              </>
            )}
          </Button>
        </form>

        <div className="border-t pt-4 mt-4">
          <p className="text-sm text-slate-500 text-center mb-3">
            ¿No tienes un código de vendedor?
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleNoCode}
          >
            <Phone className="w-4 h-4 mr-2" />
            Necesito un agente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Info box */}
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <p className="text-sm text-amber-800">
            <strong>¿Por qué necesito un código?</strong><br />
            Nuestros certificados de viaje son exclusivos y solo se venden a través de agentes autorizados 
            que te brindarán asesoría personalizada.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Component to show vendor code in checkout
export function VendorCodeBadge({ className = '' }) {
  const vendorInfo = getStoredVendorInfo();
  const vendorCode = getStoredVendorCode();
  
  if (!vendorCode) return null;
  
  return (
    <div className={`flex items-center gap-2 p-3 bg-teal-50 rounded-lg border border-teal-200 ${className}`}>
      <User className="w-5 h-5 text-teal-600" />
      <div className="flex-1">
        <p className="text-sm text-teal-700">Comprando con agente:</p>
        <p className="font-bold text-teal-800">
          {vendorInfo?.name || vendorCode}
          <span className="ml-2 font-mono text-xs bg-teal-100 px-2 py-0.5 rounded">
            {vendorCode}
          </span>
        </p>
      </div>
    </div>
  );
}
